import { Injectable, inject } from '@angular/core';
import {ToastService} from '../toast/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {
    ProjectAnalysisDefinition,
    ProjectAnalysisDefinitionResponse,
    ProjectAnalysisVersion,
    ProjectAnalysisVersionItem,
    ProjectAnalysisVersionsResponse,
    ProjectType,
} from "../../models/project-analysis";

@Injectable({
  providedIn: 'root'
})
export class ProjectAnalysisDefinitionService {
    private readonly httpClient = inject(HttpClient);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly baseUrl = `${environment.apiUrl}/project-analysis`;

    public getVersions(projectTypeId: number): Observable<ProjectAnalysisVersion[]> {
        return this.httpClient.get<ProjectAnalysisVersionsResponse>(
            `${this.baseUrl}/${projectTypeId}/definitions/versions`
        ).pipe(
            map((response) => response.map((item) => new ProjectAnalysisVersion(item))),
            catchError((error) => this.handleError(error, 'ERRORS.project_analyses_not_updated'))
        );
    }

    public getProjectDefinition(projectId: number): Observable<ProjectAnalysisDefinitionResponse> {
        return this.httpClient.get<ProjectAnalysisDefinitionResponse>(
            `${environment.apiUrl}/projects/${projectId}/definition`
        ).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_analysis_not_retrieved'))
        );
    }

    public getDefinition(projectTypeId: number, versionId: number): Observable<ProjectAnalysisDefinition> {
        return this.httpClient.get<ProjectAnalysisDefinitionResponse>(
            `${this.baseUrl}/${projectTypeId}/definitions/${versionId}`
        ).pipe(
            map((response) => new ProjectAnalysisDefinition(response)),
            catchError((error) => this.handleError(error, 'ERRORS.project_analysis_not_retrieved'))
        );
    }

    public publishDefinition(projectTypeId: number, versionId: number): Observable<void> {
        return this.httpClient.post<void>(
            `${this.baseUrl}/${projectTypeId}/definitions/${versionId}/publish`,
            {}
        ).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_analysis_not_published'))
        );
    }

    public updateDefinition(projectTypeId: number, data: ProjectAnalysisDefinition): Observable<void> {
        return this.httpClient.post<void>(
            `${this.baseUrl}/${projectTypeId}/definitions/${data.versionId}`,
            data
        ).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_analysis_not_updated'))
        );
    }

    public createFirstVersion(projectType: ProjectType): Observable<ProjectAnalysisVersion> {
        return this.httpClient.post<ProjectAnalysisVersionItem>(
            `${this.baseUrl}/${projectType.id}/definitions`,
            {}
        ).pipe(
            map((item) => new ProjectAnalysisVersion(item)),
            catchError((error: HttpErrorResponse) => this.handleError(error, 'ERRORS.project_analysis_not_created'))
        );
    }

    /**
     * Creates a new definition, using a copy of an existing definition.
     *
     * This uses the same endpoint as the `createFirstVersion` method.
     *
     * @param projectType The project type to create the definition under.
     * @param sourceProjectTypeId The source project type of the definition to copy.
     * @param sourceVersionId The source version of the definition to copy.
     */
    public copyFromVersion(projectType: ProjectType, sourceProjectTypeId: number, sourceVersionId: number): Observable<ProjectAnalysisVersion> {
        return this.httpClient.post<ProjectAnalysisVersionItem>(
            `${this.baseUrl}/${projectType.id}/definitions`,
            {
                sourceProjectTypeId,
                sourceVersionId
            }
        ).pipe(
            map((item) => new ProjectAnalysisVersion(item)),
            catchError((error: HttpErrorResponse) => this.handleError(error, 'ERRORS.project_analysis_not_copied'))
        );
    }

    public deleteVersion(projectTypeId: number, versionId: number): Observable<void> {
        return this.httpClient.delete<void>(
            `${this.baseUrl}/${projectTypeId}/definitions/${versionId}`
        ).pipe(
            catchError((error) => this.handleError(error, 'ERRORS.project_analysis_not_deleted'))
        );
    }

    private handleError(error: HttpErrorResponse, translationKey: string): Observable<never> {
        console.error(error);
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
        return throwError(() => error);
    }
}
